import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { api } from '@/lib/api';
import { fmtN, fmtRelative, shortId, cn } from '@/lib/utils';
import { PageHeader } from '@/components/PageHeader';
import { Pagination } from '@/components/Pagination';

type UserRow = {
  id: string;
  email: string;
  username: string | null;
  displayName: string;
  avatarUrl: string | null;
  preferredLanguage: string;
  role: 'user' | 'admin' | 'super_admin';
  lastSeenAt: string | null;
  createdAt: string;
  telegramId: number | null;
};

type ListEnvelope = { rows: UserRow[]; page: number; pageSize: number; total: number };

export function Users() {
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');

  const { data, isLoading } = useQuery<ListEnvelope>({
    queryKey: ['/admin/api/users', page, q],
    queryFn: () =>
      api.get<ListEnvelope>(
        `/admin/api/users?page=${page}&pageSize=50${q ? `&q=${encodeURIComponent(q)}` : ''}`,
      ),
  });

  return (
    <div>
      <PageHeader
        title="Users"
        subtitle={data ? `${fmtN(data.total)} total` : 'Loading…'}
      />

      <div className="ad-card p-3 mb-3 flex gap-2">
        <div className="flex-1 flex items-center gap-2 px-3">
          <Search size={16} className="text-text3" />
          <input
            className="bg-transparent w-full text-sm outline-none placeholder-text3"
            placeholder="Search email, username, display name…"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
          />
        </div>
      </div>

      <div className="ad-card overflow-hidden">
        <table className="ad-table">
          <thead>
            <tr>
              <th>Email / username</th>
              <th>Display name</th>
              <th>Role</th>
              <th>Lang</th>
              <th>Last seen</th>
              <th>Joined</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={7} className="text-center text-text3 py-10">
                  Loading…
                </td>
              </tr>
            ) : data && data.rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center text-text3 py-10">
                  No users.
                </td>
              </tr>
            ) : (
              data?.rows.map((u) => (
                <tr key={u.id}>
                  <td>
                    <div className="font-mono text-xs">{u.email}</div>
                    {u.username && (
                      <div className="text-text3 text-xs">@{u.username}</div>
                    )}
                  </td>
                  <td>{u.displayName}</td>
                  <td>
                    <span
                      className={cn(
                        'ad-badge',
                        u.role === 'super_admin'
                          ? 'ad-badge-accent'
                          : u.role === 'admin'
                            ? 'ad-badge-warning'
                            : 'ad-badge-neutral',
                      )}
                    >
                      {u.role}
                    </span>
                  </td>
                  <td className="text-text2">{u.preferredLanguage}</td>
                  <td className="text-text2">{fmtRelative(u.lastSeenAt)}</td>
                  <td className="text-text2">{fmtRelative(u.createdAt)}</td>
                  <td className="text-right">
                    <Link
                      to={`/users/${u.id}`}
                      className="text-accent hover:underline text-xs"
                    >
                      View
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {data && (
        <Pagination
          page={data.page}
          pageSize={data.pageSize}
          total={data.total}
          onPage={setPage}
        />
      )}
    </div>
  );
}
